import strings from "global/constants/StringConstants";
import { splitByCamelCase } from "helpers/methods";
import { RequestParticipant, SignRequest } from "models/interfaces";
import { inboxStatusTokens, InboxStatusKey } from "./Inbox.tokens";

/** What the reader can do with a row — mirrors the design's `ACTION` map. */
export type InboxRowAction = "sign" | "download";

/** The design's filter buckets. */
export type InboxTabKey =
  | "all"
  | "waiting"
  | "completed"
  | "declined"
  | "closed";

export interface InboxRowState {
  key: InboxStatusKey;
  label: string;
  /** Closed rows dim their document title in the design. */
  muted: boolean;
  /** Trailing clause on the meta line, e.g. "you've signed · awaiting 2 others". */
  note: string;
  action?: InboxRowAction;
  /**
   * Which filter pill the row belongs under. Carried explicitly rather than
   * derived from `key`, because the two don't always agree: a request you
   * delegated keeps the neutral chip but is still in flight, so it belongs
   * under "In progress" rather than "Closed".
   */
  tab: Exclude<InboxTabKey, "all">;
}

export interface InboxTabQuery {
  /** Inbox statuses the bucket is made of. */
  statuses: string[];
  /**
   * True when the status alone doesn't define the bucket, so fetched rows still
   * have to be matched against their derived `tab` — the case for the `Pending`
   * perspective split.
   */
  needsRowFilter: boolean;
}

/**
 * How each tab is fetched. Every visible tab names exactly one status and needs
 * no row filter, so all of them page one request at a time on the server (see
 * `isServerPagedTab`). The API stores one status per request and can't tell the
 * two `Pending` perspectives ("your turn" vs "waiting on others") apart, so they
 * are not split into separate tabs — "In progress" shows every pending request.
 */
export const INBOX_TAB_QUERY: Record<InboxTabKey, InboxTabQuery> = {
  all: { statuses: ["All"], needsRowFilter: false },
  waiting: { statuses: [strings.pending], needsRowFilter: false },
  completed: { statuses: [strings.signed], needsRowFilter: false },
  declined: { statuses: [strings.declined], needsRowFilter: false },
  closed: {
    statuses: [strings.voided, strings.expired],
    needsRowFilter: false,
  },
};

/**
 * Whether the API can serve this tab one page at a time. Every visible tab does
 * (one status, no row filter). Only the internal "closed" bucket spans more than
 * one status, and it is never a selectable tab.
 */
export const isServerPagedTab = (tab: InboxTabKey): boolean => {
  const query = INBOX_TAB_QUERY[tab];
  return query.statuses.length === 1 && !query.needsRowFilter;
};

const isDeclinedStatus = (status?: string) =>
  status === strings.declined || status === strings.rejected;

/**
 * A signer declining (or a reviewer rejecting) kills the request for everyone,
 * but the request's own status can still read `Pending` — so check the
 * participants too, the same way the signer screen's `ShowRequestStatus` does.
 */
export const isRequestDeclined = (request: SignRequest) => {
  const isClosedBy = (status?: string) =>
    status?.toLowerCase() === strings.declined.toLowerCase() ||
    status?.toLowerCase() === strings.rejected.toLowerCase();
  return (
    isDeclinedStatus(request.status) ||
    (request.signers ?? []).some((signer: RequestParticipant) =>
      isClosedBy(signer.status),
    ) ||
    (request.approval?.reviewers ?? []).some((reviewer: any) =>
      isClosedBy(reviewer?.status),
    )
  );
};

const isDelegatedStatus = (status?: string) =>
  status === strings.delegated || status === strings.Delegated;

/** Signers still owing a signature — CC recipients and delegates don't count. */
const countAwaiting = (signers: RequestParticipant[]) =>
  signers.filter(
    (signer) =>
      !signer.cc &&
      !isDelegatedStatus(signer.status) &&
      signer.status !== strings.signed,
  ).length;

/**
 * Maps a `SignRequest` onto the design's signer-perspective vocabulary.
 *
 * The app stores one status per request; the design splits `Pending` in two —
 * "Your turn to sign" when the reader is a current signer and "Waiting for
 * others" otherwise — so `currentSigners` decides which chip a pending row
 * gets. Statuses the design has no chip for keep their own label under the
 * neutral treatment so no information is lost.
 */
export const getInboxRowState = (
  request: SignRequest,
  userEmail: string,
): InboxRowState => {
  const signers: RequestParticipant[] = request.signers ?? [];
  const mySignerStatus = signers.find(
    (signer: RequestParticipant) => signer.email === userEmail,
  )?.status;

  if (isRequestDeclined(request)) {
    return {
      key: "declined",
      label: inboxStatusTokens.declined.label,
      muted: inboxStatusTokens.declined.muted,
      note: isDeclinedStatus(mySignerStatus)
        ? strings.inboxRowNotes.declined
        : "",
      tab: "declined",
    };
  }

  if (
    request.status === strings.signed ||
    request.status === strings.completed
  ) {
    return {
      key: "completed",
      label: inboxStatusTokens.completed.label,
      muted: inboxStatusTokens.completed.muted,
      note: "",
      action: "download",
      tab: "completed",
    };
  }

  if (request.status === strings.expired) {
    return {
      key: "expired",
      label: inboxStatusTokens.expired.label,
      muted: inboxStatusTokens.expired.muted,
      note: "",
      tab: "closed",
    };
  }

  if (request.status === strings.voided) {
    return {
      key: "void",
      label: inboxStatusTokens.void.label,
      muted: inboxStatusTokens.void.muted,
      note: strings.inboxRowNotes.voided,
      tab: "closed",
    };
  }

  if (request.status === strings.pending) {
    if (isDelegatedStatus(mySignerStatus)) {
      return {
        key: "void",
        label: strings.inboxStatusLabels.delegatedByYou,
        muted: inboxStatusTokens.void.muted,
        note: "",
        tab: "waiting",
      };
    }

    if (request.currentSigners?.includes(userEmail)) {
      // Still surfaces the "your turn" chip + Sign action, but files under the
      // single "In progress" (pending) tab — the API can't separate it from the
      // "waiting on others" pending rows.
      return {
        key: "action",
        label: inboxStatusTokens.action.label,
        muted: inboxStatusTokens.action.muted,
        note: "",
        action: "sign",
        tab: "waiting",
      };
    }

    const awaiting = countAwaiting(signers);
    const awaitingNote = awaiting
      ? `${strings.inboxRowNotes.awaiting} ${awaiting} ${
          awaiting === 1
            ? strings.inboxRowNotes.otherSingular
            : strings.inboxRowNotes.otherPlural
        }`
      : "";

    return {
      key: "waiting",
      label: inboxStatusTokens.waiting.label,
      muted: inboxStatusTokens.waiting.muted,
      note:
        mySignerStatus === strings.signed
          ? [strings.inboxRowNotes.youSigned, awaitingNote]
              .filter(Boolean)
              .join(" · ")
          : awaitingNote,
      tab: "waiting",
    };
  }

  // Scheduled, InReview and anything the design doesn't cover read as neutral
  // but keep their own label. They are still moving, so they file under
  // "In progress".
  return {
    key: "void",
    label: splitByCamelCase(request.status),
    muted: inboxStatusTokens.void.muted,
    note: "",
    tab: "waiting",
  };
};
